/**
 * Playback state and projection.
 *
 * A room does not broadcast "we are at 01:23:45". By the time that message
 * arrives it is already stale. Instead it broadcasts a *line*: a position, the
 * server time at which the media held that position, and the rate it is moving
 * at. Any client can then evaluate that line at any moment to learn where
 * playback should be right now.
 *
 *     position(T) = anchorPositionMs + (T - anchorServerMs) x rate
 *
 * This is why sync survives latency. A message delayed by 300 ms is not wrong,
 * it just needs evaluating 300 ms further along.
 */

/**
 * The authoritative description of what a room is doing.
 *
 * Immutable by construction. Transitions produce a new state rather than
 * mutating this one, which keeps the reducer honest and makes state history
 * trivial to record for diagnostics.
 */
export interface PlaybackState {
  /** Media position at `anchorServerMs`, in milliseconds. */
  readonly anchorPositionMs: number;
  /** Server time at which the media held `anchorPositionMs`. */
  readonly anchorServerMs: number;
  /** Playback speed multiplier as chosen by the user, e.g. 1 or 1.5. */
  readonly rate: number;
  /** Whether the media is currently held still. */
  readonly paused: boolean;
  /**
   * Server time at which playback is scheduled to begin, or `null` if it is
   * not waiting on a scheduled start.
   *
   * This field is the reason two machines start together rather than one after
   * the other. A play command names a moment slightly in the future; both
   * clients hold position until that moment arrives, then release at once.
   */
  readonly startAtServerMs: number | null;
  /** Total media duration if known, used to clamp projection. */
  readonly durationMs: number | null;
}

/** A room that has been created but has not yet been told to play anything. */
export function idlePlaybackState(atServerMs: number): PlaybackState {
  return {
    anchorPositionMs: 0,
    anchorServerMs: atServerMs,
    rate: 1,
    paused: true,
    startAtServerMs: null,
    durationMs: null,
  };
}

/**
 * Where playback should be at the given server time.
 *
 * Handles three cases beyond the plain linear projection:
 *   - paused media does not advance
 *   - media awaiting a scheduled start holds at the anchor until that moment
 *   - projection never runs past the end of the media or before its start
 */
export function projectPosition(state: PlaybackState, atServerMs: number): number {
  const { anchorPositionMs, anchorServerMs, rate, paused, startAtServerMs, durationMs } = state;

  if (paused) return clampToMedia(anchorPositionMs, durationMs);

  // While a scheduled start is pending, the anchor is the start moment itself.
  const effectiveEpochMs = startAtServerMs ?? anchorServerMs;
  if (atServerMs <= effectiveEpochMs) return clampToMedia(anchorPositionMs, durationMs);

  const elapsedMs = atServerMs - effectiveEpochMs;
  return clampToMedia(anchorPositionMs + elapsedMs * rate, durationMs);
}

/** True when the state describes media that is counting down to a start. */
export function isAwaitingStart(state: PlaybackState, atServerMs: number): boolean {
  return !state.paused && state.startAtServerMs !== null && atServerMs < state.startAtServerMs;
}

/** True when the media should be actively advancing right now. */
export function isAdvancing(state: PlaybackState, atServerMs: number): boolean {
  return !state.paused && !isAwaitingStart(state, atServerMs);
}

/**
 * Rewrite a state so its anchor sits at `atServerMs` without changing what the
 * state means.
 *
 * Long-lived anchors accumulate floating point error and make debugging harder,
 * because the anchor drifts ever further from the present. Re-anchoring keeps
 * the arithmetic close to zero and the diagnostics readable.
 */
export function reanchor(state: PlaybackState, atServerMs: number): PlaybackState {
  if (isAwaitingStart(state, atServerMs)) return state;
  return {
    ...state,
    anchorPositionMs: projectPosition(state, atServerMs),
    anchorServerMs: atServerMs,
    startAtServerMs: null,
  };
}

function clampToMedia(positionMs: number, durationMs: number | null): number {
  if (positionMs < 0) return 0;
  if (durationMs !== null && positionMs > durationMs) return durationMs;
  return positionMs;
}
